import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { APIS, SF_XTRN_USR_INFO, URLS } from "@/tra/tancis/ptli/usr/xtrnusr/services/PtliXtrnUsrService";
import { getNameOfJSDocTypedef } from "typescript";

/*
 * External User Details
 * !== 외부 사용자 상세 ==!
 */
export const PTLI0922004Q = (): JSX.Element => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0001", "auth_ptli0015", "auth_ptlianyADM"],
    });
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { xtrnUsrUuid } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [rtrnYn, setRtrnYn] = useState("N");
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0021" },
            { area: "comnCd", comnCd: "PTL0001" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // External User Information !== 외부 사용자 정보 ==!
        xtrnUsrInfo: useForm({
            defaultSchema: SF_XTRN_USR_INFO,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get External User Information !== 외부 사용자 정보 조회 ==!
        getXtrnUsrInfo: useFetch({
            api: () => APIS.getXtrnUsrInfo(xtrnUsrUuid),
            enabled: !!xtrnUsrUuid,
            onSuccess: (data) => {
                const xtrnUsrInfo = data.xtrnUsrInfo.content;
                form.xtrnUsrInfo.setValues(xtrnUsrInfo);
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Delete External User Information !== 외부 사용자 정보 삭제 ==!
        deleteXtrnUsrInfo: useFetch({
            api: (xtrnUsrUuid) => APIS.deleteXtrnUsrInfo(xtrnUsrUuid),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.00003",
                    onCancel: () => {
                        navigate(`${URLS.ptli0922001q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        // Update External User Information !== 외부 사용자 정보 수정 ==!
        updateXtrnUsrInfo: useFetch({
            api: (info: any) => APIS.updateXtrnUsrInfo(info),
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
                navigate(`${URLS.ptli0922001q}?menuId=${menuId}`);
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),

        // Reset the External User password. !== 외부 사용자 비밀번호를 초기화 ==!
        updateXtrnUsrPwdInit: useFetch({
            api: (data) => APIS.updateXtrnUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: "msg.ptl.00764", // A temporary password has been sent. !==  임시 비밀번호가 발송되었습니다. ==!
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete External User Information !== 외부 사용자 정보 삭제 ==!
        onDeleteBtnClick: () => {
            modal.openModal({
                content: "msg.00103",
                onConfirm: () => {
                    fetch.deleteXtrnUsrInfo.fetch(xtrnUsrUuid);
                },
            });
        },

        // Click Update Button !== 수정 버튼 클릭 ==!
        onUpdateBtnClick: () => {
            if (!comnUtils.isEmpty(form.xtrnUsrInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }

            // save Information of Organization !== 기관 정보 저장 ==!
            form.xtrnUsrInfo.handleSubmit(
                () => {
                    const data = form.xtrnUsrInfo.getValues();

                    modal.openModal({
                        content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.updateXtrnUsrInfo.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.ptl.00749", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.xtrnUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.xtrnUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.xtrnUsrInfo.getValues();

                            fetch.updateXtrnUsrPwdInit.fetch(usrAplyInfo);
                        },
                        () => {
                            toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        },
                    )();
                },
            });
        },
    };

    useEffect(() => {
        fetch.getXtrnUsrInfo.fetch();
        form.xtrnUsrInfo.setEditable(false);
    }, []);

    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_USR_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.xtrnUsrInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.frgnrYn}></Group.Control>
                                <Group.Control {...form.xtrnUsrInfo.schema.aplcCntyCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.usrMoblNo}></Group.Control>
                                <Group.Control {...form.xtrnUsrInfo.schema.bidrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.usrEmlAddr}></Group.Control>
                                <Group.Control {...form.xtrnUsrInfo.schema.usrStatCd}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.rsdtAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.xtrnUsrInfo.schema.mdfyRsn}></Group.Control>
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli0922001q);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role="edit"
                        onClick={() => {
                            navigate(`${URLS.ptli0922003s}/${xtrnUsrUuid}`);
                        }}
                    ></Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};
